import ApartmentIcon from '@mui/icons-material/Apartment'
import {
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import * as endPoints from '@/endpoints'
import { groupTypeLabel } from '@/features/dashboard/site-admin/orgGroupTypes'
import axios from '@/lib/axios'
import EntityLink from '../EntityLink'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'

function Empty({ children }) {
    return (
        <Typography variant="body2" color="text.secondary">
            {children}
        </Typography>
    )
}

Empty.propTypes = { children: PropTypes.node }

/**
 * A group of organisations (a trust, local authority, portfolio), for site
 * admins: which organisations belong to it and who administers it. Uses the
 * same query as the Groups page's Manage dialog, so its edits show here.
 */
export default function GroupPeek({ id }) {
    const query = useQuery({
        queryKey: ['siteAdmin', 'orgGroup', id],
        queryFn: () =>
            axios
                .get(endPoints.ADMIN_ORG_GROUP(id))
                .then((response) => response.data),
        retry: false
    })
    const group = query.data

    if (!group) {
        return <PeekPlaceholder kind="Group" loading={query.isLoading} />
    }

    const members = group.members ?? []
    const admins = group.admins ?? []

    return (
        <PeekLayout
            kind="Group"
            title={group.name}
            subtitle={groupTypeLabel(group.group_type)}
            actions={
                <Button
                    component={RouterLink}
                    to="/site-admin/groups"
                    size="small"
                    variant="outlined"
                    startIcon={<ApartmentIcon fontSize="small" />}
                >
                    Manage groups
                </Button>
            }
        >
            <PeekSection title="Details">
                <PeekDetails
                    rows={[
                        {
                            label: 'Status',
                            value: (
                                <Chip
                                    size="small"
                                    color={
                                        group.is_enabled ? 'success' : 'default'
                                    }
                                    label={
                                        group.is_enabled
                                            ? 'Enabled'
                                            : 'Disabled'
                                    }
                                />
                            )
                        },
                        { label: 'Organisations', value: members.length },
                        { label: 'Admins', value: admins.length }
                    ]}
                />
            </PeekSection>

            <PeekSection title={`Organisations (${members.length})`}>
                {members.length > 0 ? (
                    <List dense disablePadding>
                        {members.map((member) => (
                            <ListItem key={member.org_id} disableGutters>
                                <ListItemText
                                    primary={
                                        <EntityLink
                                            type="organisation"
                                            id={member.org_id}
                                        >
                                            {member.org_name}
                                        </EntityLink>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Empty>No organisations in this group yet.</Empty>
                )}
            </PeekSection>

            <PeekSection title={`Admins (${admins.length})`}>
                {admins.length > 0 ? (
                    <List dense disablePadding>
                        {admins.map((admin) => (
                            <ListItem key={admin.user_id} disableGutters>
                                <ListItemText
                                    primary={
                                        <EntityLink
                                            type="user"
                                            id={admin.user_id}
                                        >
                                            {admin.name}
                                        </EntityLink>
                                    }
                                    secondary={
                                        <>
                                            {admin.email}
                                            {admin.org_name ? (
                                                <>
                                                    {' · '}
                                                    <EntityLink
                                                        type="organisation"
                                                        id={admin.org_id}
                                                    >
                                                        {admin.org_name}
                                                    </EntityLink>
                                                </>
                                            ) : null}
                                        </>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Empty>No group admins yet.</Empty>
                )}
            </PeekSection>
        </PeekLayout>
    )
}

GroupPeek.propTypes = {
    id: PropTypes.string.isRequired
}
